import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import Button from './Button';
import Input from './Input';
import Modal from './Modal';
import { useSnackbar } from '../../contexts/SnackbarContext';
import type GroupModel from '../../models/Group';

// --- Icons -----------------------------------------------------------------

const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const CrossIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
);

const SelectAllTickIcon = () => (
    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M2.5 6l2.5 3 4.5-5.5" />
    </svg>
);

const SelectAllPartialCrossIcon = () => (
    <svg className="h-3 w-3 text-gray-400" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
        <path d="M3 3l6 6M9 3l-6 6" />
    </svg>
);

// --- Tag filter helpers (shared) -----------------------------------------

/** `tags=[a,b]` requires entity to have every listed tag (case-insensitive). */
function parseTagsFromFilter(searchTerm: string): { requiredTags: string[]; rest: string } {
    const requiredTags: string[] = [];
    const seenLower = new Set<string>();
    const rest = searchTerm
        .replace(/tags=\[([^\]]*)\]/gi, (_match, inner: string) => {
            for (const part of (inner ?? '').split(',').map(s => s.trim()).filter(Boolean)) {
                const low = part.toLowerCase();
                if (!seenLower.has(low)) {
                    seenLower.add(low);
                    requiredTags.push(part);
                }
            }
            return ' ';
        })
        .replace(/\s+/g, ' ')
        .trim();
    return { requiredTags, rest };
}

function appendRequiredTagToFilter(current: string, tagToAdd: string): string {
    const trimmed = tagToAdd.trim();
    if (!trimmed) return current;
    const { requiredTags, rest } = parseTagsFromFilter(current);
    const lowerNew = trimmed.toLowerCase();
    const merged = requiredTags.filter(Boolean);
    if (!merged.some(t => t.toLowerCase() === lowerNew)) {
        merged.push(trimmed);
    }
    const clause = `tags=[${merged.join(',')}]`;
    const base = rest.trim();
    return base ? `${base} ${clause}` : clause;
}

function removeRequiredTagFromFilter(current: string, tagToRemove: string): string {
    const trimmed = tagToRemove.trim();
    if (!trimmed) return current;
    const { requiredTags, rest } = parseTagsFromFilter(current);
    const lowerRm = trimmed.toLowerCase();
    const merged = requiredTags.filter(t => t.toLowerCase() !== lowerRm);
    const base = rest.trim();
    if (merged.length === 0) {
        return base;
    }
    const clause = `tags=[${merged.join(',')}]`;
    return base ? `${base} ${clause}` : clause;
}

function renderTagWithFilterHighlight(
    tag: string,
    filter: string,
    tagInRequiredFilter = false
): React.ReactNode {
    const q = filter.trim();
    const matchHoverClass = tagInRequiredFilter
        ? 'group-hover:text-red-700'
        : 'group-hover:text-blue-700';
    if (!q) return tag;
    const lowerTag = tag.toLowerCase();
    const lowerQ = q.toLowerCase();
    const idx = lowerTag.indexOf(lowerQ);
    if (idx === -1) return tag;
    const before = tag.slice(0, idx);
    const match = tag.slice(idx, idx + lowerQ.length);
    const after = tag.slice(idx + lowerQ.length);
    return (
        <>
            {before}
            <span className={`font-semibold text-gray-700 ${matchHoverClass}`}>{match}</span>
            {after}
        </>
    );
}

// --- Public types ----------------------------------------------------------

export interface Teacher {
    id: number;
    name: string;
    email?: string;
    tags?: string[];
}

export interface TeacherSelectorProps {
    teachers: Teacher[];
    selectedTeachers: Teacher[];
    onChange: (teachers: Teacher[]) => void;
    onAddTeacher?: (teacherData: { name: string; email: string; teacher_code: string }) => Promise<any>;
    placeholder?: string;
    disabled?: boolean;
}

export type GroupSelection = number | string | { id: number; name?: string; group_code?: string };

export interface GroupSelectorProps {
    groups: GroupModel[];
    selectedGroups: GroupSelection[];
    onChange: (selectedGroups: GroupSelection[]) => void;
    onAddGroup?: (groupData: { name: string; group_code?: string }) => Promise<GroupModel>;
    placeholder?: string;
    disabled?: boolean;
}

// --- Internal row model ----------------------------------------------------

type SelectorKind = 'teacher' | 'group';

interface SelectorRow {
    id: number;
    label: string;
    tags: string[];
    teacher?: Teacher;
    group?: GroupModel;
}

function rowsFromTeachers(teachers: Teacher[]): SelectorRow[] {
    return teachers
        .filter(t => t.name && t.name.trim() !== '')
        .map(t => ({
            id: t.id,
            label: t.name,
            tags: Array.isArray(t.tags) ? t.tags.filter((x): x is string => typeof x === 'string') : [],
            teacher: t,
        }));
}

function rowsFromGroups(groups: GroupModel[]): SelectorRow[] {
    return groups
        .filter(g => g.name && g.name.trim() !== '' && g.id != null)
        .map(g => ({
            id: g.id!,
            label: g.name,
            tags: Array.isArray(g.tags) ? g.tags.filter((x): x is string => typeof x === 'string') : [],
            group: g,
        }));
}

/** Stable order for the dropdown: selected rows first (in list order), then the rest. */
function rowIdsSelectedFirst(rows: SelectorRow[], selectedIds: number[]): number[] {
    const selected = new Set(selectedIds);
    const sel: number[] = [];
    const unsel: number[] = [];
    for (const row of rows) {
        if (selected.has(row.id)) sel.push(row.id);
        else unsel.push(row.id);
    }
    return [...sel, ...unsel];
}

function rowMatchesFilter(row: SelectorRow, parsedFilter: ReturnType<typeof parseTagsFromFilter>): boolean {
    const { requiredTags, rest } = parsedFilter;
    const normalizedFree = rest.trim().toLowerCase();
    const tags = row.tags;
    const tagsLower = new Set(tags.map(t => t.toLowerCase()));

    if (requiredTags.length > 0) {
        const hasAll = requiredTags.every(req => tagsLower.has(req.trim().toLowerCase()));
        if (!hasAll) return false;
    }

    if (!normalizedFree) {
        return true;
    }

    if (row.teacher) {
        const nameMatch = row.teacher.name?.toLowerCase().includes(normalizedFree) ?? false;
        const emailMatch = row.teacher.email?.toLowerCase().includes(normalizedFree) ?? false;
        const tagMatch = tags.some(tag => tag.toLowerCase().includes(normalizedFree));
        return nameMatch || emailMatch || tagMatch;
    }

    if (row.group) {
        const nameMatch = row.group.name?.toLowerCase().includes(normalizedFree) ?? false;
        const codeMatch = row.group.group_code?.toLowerCase().includes(normalizedFree) ?? false;
        const tagMatch = tags.some(tag => tag.toLowerCase().includes(normalizedFree));
        return nameMatch || codeMatch || tagMatch;
    }

    return false;
}

// --- Unified selector --------------------------------------------------------

interface UnifiedEntitySelectorBase {
    kind: SelectorKind;
    rows: SelectorRow[];
    placeholder: string;
    disabled: boolean;
    panelDomId: string;
    searchPlaceholder: string;
    addRowLabel: string;
    ariaLabel: string;
    title: string;
    emptyListMessage: string;
    selectAllAriaPrefix: string;
}

interface UnifiedTeacherBranch extends UnifiedEntitySelectorBase {
    kind: 'teacher';
    teachers: Teacher[];
    selectedTeachers: Teacher[];
    onChange: (teachers: Teacher[]) => void;
    onAddTeacher?: TeacherSelectorProps['onAddTeacher'];
}

interface UnifiedGroupBranch extends UnifiedEntitySelectorBase {
    kind: 'group';
    groups: GroupModel[];
    selectedGroups: GroupSelection[];
    onChange: (groups: GroupSelection[]) => void;
    onAddGroup?: GroupSelectorProps['onAddGroup'];
}

type UnifiedEntitySelectorProps = UnifiedTeacherBranch | UnifiedGroupBranch;

function selectionIdsFromProps(props: UnifiedEntitySelectorProps): number[] {
    if (props.kind === 'teacher') {
        return props.selectedTeachers.map(t => t.id);
    }
    return props.selectedGroups.map(g => {
        if (typeof g === 'object' && g !== null) return (g as { id: number }).id;
        return Number(g);
    });
}

function UnifiedEntitySelector(props: UnifiedEntitySelectorProps) {
    const { showSuccess, showError } = useSnackbar();
    const [isOpen, setIsOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newTeacherData, setNewTeacherData] = useState({ name: '', email: '' });
    const [newGroupData, setNewGroupData] = useState({ name: '' });
    const [isAdding, setIsAdding] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    /** Snapshot when the panel opens; used to keep selected entries at the top without live reshuffling. */
    const [openedRowOrder, setOpenedRowOrder] = useState<number[] | null>(null);
    const [openedScrollTargetId, setOpenedScrollTargetId] = useState<number | null>(null);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const listScrollRef = useRef<HTMLDivElement>(null);
    const selectedTeachersRef = useRef<Teacher[]>([]);
    const selectedGroupsRef = useRef<GroupSelection[]>([]);
    const teachersRef = useRef<Teacher[]>([]);

    useEffect(() => {
        if (props.kind === 'teacher') {
            selectedTeachersRef.current = props.selectedTeachers;
            teachersRef.current = props.teachers;
        } else {
            selectedGroupsRef.current = props.selectedGroups;
        }
    }, [props]);

    const generateTeacherCode = useCallback((name: string): string => {
        if (!name) return `teacher-${uuidv4().substring(0, 4)}`;
        const baseCode = name.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        const shortSuffix = uuidv4().substring(0, 4);
        return `${baseCode.substring(0, 15)}-${shortSuffix}`;
    }, []);

    const generateGroupCode = useCallback((name: string): string => {
        if (!name) return `group-${uuidv4().substring(0, 4)}`;
        const baseCode = name.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        const shortSuffix = uuidv4().substring(0, 4);
        return `${baseCode.substring(0, 15)}-${shortSuffix}`;
    }, []);

    const updatePanelPosition = useCallback(() => {
        if (!buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const panelHeightEstimate = 240;
        const margin = 4;

        let top: number | 'auto' = 'auto';
        let bottom: number | 'auto' = 'auto';

        if (spaceBelow >= panelHeightEstimate || spaceBelow >= spaceAbove) {
            top = rect.bottom + window.scrollY + margin;
        } else {
            bottom = window.innerHeight - rect.top - window.scrollY + margin;
        }

        setPanelStyle({
            position: 'absolute',
            top: top === 'auto' ? 'auto' : `${top}px`,
            bottom: bottom === 'auto' ? 'auto' : `${bottom}px`,
            left: `${rect.left + window.scrollX}px`,
            width: `${rect.width}px`,
            zIndex: 1100,
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return;

        updatePanelPosition();

        const handleResize = () => updatePanelPosition();
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
        };
    }, [isOpen, updatePanelPosition]);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setOpenedRowOrder(null);
            setOpenedScrollTargetId(null);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const panelElement = document.getElementById(props.panelDomId);
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                (!panelElement || !panelElement.contains(event.target as Node))
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, props.panelDomId]);

    const selectedIds = useMemo(() => selectionIdsFromProps(props), [props]);

    const handleOptionToggle = useCallback((row: SelectorRow) => {
        if (props.disabled) return;
        if (props.kind === 'teacher' && row.teacher) {
            const { selectedTeachers, onChange } = props;
            const isSelected = selectedTeachers.some(t => t.id === row.id);
            const next = isSelected
                ? selectedTeachers.filter(t => t.id !== row.id)
                : [...selectedTeachers, row.teacher];
            onChange(next);
            return;
        }
        if (props.kind === 'group' && row.group) {
            const { selectedGroups, onChange } = props;
            const groupId = row.group.id!;
            const isSelected = selectedGroups.some(g => {
                if (typeof g === 'object' && g !== null) return (g as { id: number }).id === groupId;
                return g === groupId;
            });
            let next: GroupSelection[];
            if (isSelected) {
                next = selectedGroups.filter(g => {
                    if (typeof g === 'object' && g !== null) return (g as { id: number }).id !== groupId;
                    return g !== groupId;
                });
            } else {
                next = [...selectedGroups, {
                    id: row.group.id!,
                    name: row.group.name,
                    group_code: row.group.group_code,
                }];
            }
            onChange(next);
        }
    }, [props]);

    const handleClearSelection = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (props.disabled) return;
        if (props.kind === 'teacher') {
            props.onChange([]);
        } else {
            props.onChange([]);
        }
        setIsOpen(false);
    };

    const handleAddClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (props.disabled) return;
        setIsAddModalOpen(true);
        setIsOpen(false);
    };

    const handleAddTeacherInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewTeacherData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddGroupInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewGroupData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveNewTeacher = async () => {
        if (!newTeacherData.name || !newTeacherData.email) {
            showError('Name and email are required.');
            return;
        }

        if (!/\S+@\S+\.\S+/.test(newTeacherData.email)) {
            showError('Please enter a valid email address.');
            return;
        }

        if (props.kind !== 'teacher' || !props.onAddTeacher) {
            showError('Add teacher functionality is not available.');
            return;
        }

        setIsAdding(true);

        try {
            const generatedCode = generateTeacherCode(newTeacherData.name.trim());
            const dataToSend = {
                name: newTeacherData.name.trim(),
                email: newTeacherData.email.trim(),
                teacher_code: generatedCode
            };

            const newTeacher = await props.onAddTeacher(dataToSend);

            showSuccess('Teacher added successfully!');
            setIsAddModalOpen(false);

            const teacherName = newTeacherData.name.trim();
            const teacherEmail = newTeacherData.email.trim();
            setNewTeacherData({ name: '', email: '' });

            const attemptToSelectTeacher = (attemptNumber = 0) => {
                const currentTeachers = teachersRef.current;

                let teacherToAdd: Teacher | undefined;

                if (newTeacher && newTeacher.id) {
                    teacherToAdd = {
                        id: newTeacher.id,
                        name: newTeacher.name || teacherName,
                        email: newTeacher.email || teacherEmail
                    };
                } else {
                    teacherToAdd = currentTeachers.find(t =>
                        t.name === teacherName || t.email === teacherEmail
                    );
                }

                if (teacherToAdd) {
                    props.onChange([...selectedTeachersRef.current, teacherToAdd]);
                } else if (attemptNumber < 5) {
                    setTimeout(() => attemptToSelectTeacher(attemptNumber + 1), 100);
                } else {
                    console.warn('[EntitySelector] Could not find newly added teacher after multiple attempts');
                }
            };

            setTimeout(() => attemptToSelectTeacher(), 100);
        } catch (error) {
            console.error('Error adding teacher:', error);
            showError(`Failed to add teacher: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsAdding(false);
        }
    };

    const handleSaveNewGroup = async () => {
        if (!newGroupData.name.trim()) {
            showError('Group name is required.');
            return;
        }

        if (props.kind !== 'group' || !props.onAddGroup) {
            showError('Add group functionality is not available.');
            return;
        }

        setIsAdding(true);

        try {
            const generatedCode = generateGroupCode(newGroupData.name.trim());
            const newGroup = await props.onAddGroup({
                name: newGroupData.name.trim(),
                group_code: generatedCode
            });

            if (newGroup) {
                showSuccess(`Group "${newGroup.name}" added successfully!`);
                setIsAddModalOpen(false);
                setNewGroupData({ name: '' });

                const groupForSelection: GroupSelection = {
                    id: newGroup.id!,
                    name: newGroup.name,
                    group_code: newGroup.group_code
                };
                props.onChange([...selectedGroupsRef.current, groupForSelection]);
            }
        } catch (error) {
            console.error('Error adding group:', error);
            showError('Failed to add group. Please try again.');
        } finally {
            setIsAdding(false);
        }
    };

    const getButtonText = () => {
        if (props.kind === 'teacher') {
            const { selectedTeachers, placeholder } = props;
            if (selectedTeachers.length === 0) return placeholder;
            if (selectedTeachers.length === 1) return selectedTeachers[0]?.name || placeholder;
            return `${selectedTeachers.length} selected`;
        }
        const { selectedGroups, groups, placeholder } = props;
        if (selectedGroups.length === 0) return placeholder;
        if (selectedGroups.length === 1) {
            const first = selectedGroups[0];
            const id = typeof first === 'object' && first !== null ? (first as { id: number }).id : first;
            const group = groups.find((g: GroupModel) => g.id === id);
            return group?.name || 'Selected Group';
        }
        return `${selectedGroups.length} groups selected`;
    };

    const parsedFilter = useMemo(() => parseTagsFromFilter(searchTerm), [searchTerm]);

    const filteredRows = useMemo(() => {
        if (!searchTerm.trim()) {
            return props.rows;
        }
        return props.rows.filter(row => rowMatchesFilter(row, parsedFilter));
    }, [props.rows, searchTerm, parsedFilter]);

    const displayRows = useMemo(() => {
        if (!openedRowOrder || openedRowOrder.length === 0) {
            return filteredRows;
        }
        const indexMap = new Map(openedRowOrder.map((id, idx) => [id, idx]));
        return [...filteredRows].sort((a, b) => {
            const ia = indexMap.get(a.id);
            const ib = indexMap.get(b.id);
            if (ia === undefined && ib === undefined) return 0;
            if (ia === undefined) return 1;
            if (ib === undefined) return -1;
            return ia - ib;
        });
    }, [filteredRows, openedRowOrder]);

    useLayoutEffect(() => {
        if (!isOpen || openedScrollTargetId == null) return;

        const scrollContainer = listScrollRef.current;
        const selectedElement = scrollContainer?.querySelector<HTMLElement>(
            `[data-selector-row-id="${openedScrollTargetId}"]`
        );
        if (!scrollContainer || !selectedElement) return;

        const nextScrollTop = selectedElement.offsetTop - Math.max(
            0,
            (scrollContainer.clientHeight - selectedElement.offsetHeight) / 2
        );
        scrollContainer.scrollTop = Math.max(0, nextScrollTop);
    }, [displayRows, isOpen, openedScrollTargetId]);

    const applyTagChipAction = useCallback((tag: string) => {
        setSearchTerm(prev => {
            const { requiredTags } = parseTagsFromFilter(prev);
            const inFilter = requiredTags.some(
                t => t.toLowerCase() === tag.trim().toLowerCase()
            );
            return inFilter ? removeRequiredTagFromFilter(prev, tag) : appendRequiredTagToFilter(prev, tag);
        });
    }, []);

    const filteredSelectAllState = useMemo((): 'none' | 'some' | 'all' => {
        const n = filteredRows.length;
        if (n === 0) return 'none';
        let selected = 0;
        for (const row of filteredRows) {
            if (selectedIds.includes(row.id)) selected++;
        }
        if (selected === 0) return 'none';
        if (selected === n) return 'all';
        return 'some';
    }, [filteredRows, selectedIds]);

    const handleToggleSelectAllFiltered = useCallback(
        (e: React.SyntheticEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (filteredRows.length === 0 || props.disabled) return;
            const filteredIds = new Set(filteredRows.map(r => r.id));

            if (props.kind === 'teacher') {
                if (filteredSelectAllState === 'all') {
                    props.onChange(props.selectedTeachers.filter(t => !filteredIds.has(t.id)));
                } else {
                    const selectedIdSet = new Set(props.selectedTeachers.map(t => t.id));
                    const toAdd = filteredRows
                        .filter(r => r.teacher && !selectedIdSet.has(r.id))
                        .map(r => r.teacher!);
                    props.onChange([...props.selectedTeachers, ...toAdd]);
                }
                return;
            }

            if (filteredSelectAllState === 'all') {
                props.onChange(props.selectedGroups.filter(g => {
                    const id = typeof g === 'object' && g !== null ? (g as { id: number }).id : Number(g);
                    return !filteredIds.has(id);
                }));
            } else {
                const selectedIdSet = new Set(selectedIds);
                const toAdd: GroupSelection[] = filteredRows
                    .filter(r => r.group && !selectedIdSet.has(r.id))
                    .map(r => ({
                        id: r.group!.id!,
                        name: r.group!.name,
                        group_code: r.group!.group_code,
                    }));
                props.onChange([...props.selectedGroups, ...toAdd]);
            }
        },
        [filteredRows, filteredSelectAllState, props, selectedIds]
    );

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    }, []);

    const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && displayRows.length > 0) {
            e.preventDefault();
            handleOptionToggle(displayRows[0]!);
        }
    }, [displayRows, handleOptionToggle]);

    const showAdd = props.kind === 'teacher' ? !!props.onAddTeacher : !!props.onAddGroup;

    const DropdownPanel = isOpen ? (
        <div
            id={props.panelDomId}
            style={panelStyle}
            className="bg-white shadow-lg rounded-md border border-gray-200 flex flex-col overflow-hidden"
        >
            <div className="p-2 border-b border-gray-200 bg-gray-50">
                <Input
                    id={`${props.panelDomId}-search`}
                    name={`${props.panelDomId}-search`}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={props.searchPlaceholder}
                    autoFocus
                    required={false}
                    minLength={0}
                    label=""
                />
            </div>
            {filteredRows.length > 0 && (
                <div
                    role="checkbox"
                    tabIndex={0}
                    aria-checked={
                        filteredSelectAllState === 'all'
                            ? true
                            : filteredSelectAllState === 'some'
                                ? 'mixed'
                                : false
                    }
                    aria-label={
                        filteredSelectAllState === 'all'
                            ? `Deselect all ${props.selectAllAriaPrefix} currently shown in the list`
                            : `Select all ${props.selectAllAriaPrefix} currently shown in the list`
                    }
                    className="px-3 py-2 border-b border-gray-200 bg-white flex items-center min-w-0 cursor-pointer hover:bg-gray-100 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        handleToggleSelectAllFiltered(e);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggleSelectAllFiltered(e);
                        }
                    }}
                >
                    <span
                        className={[
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border mr-2 pointer-events-none',
                            filteredSelectAllState === 'all'
                                ? 'border-blue-600 bg-blue-600 text-white'
                                : 'border-gray-300 bg-white',
                        ].join(' ')}
                        aria-hidden
                    >
                        {filteredSelectAllState === 'all' ? <SelectAllTickIcon /> : null}
                        {filteredSelectAllState === 'some' ? <SelectAllPartialCrossIcon /> : null}
                    </span>
                    <span className="truncate select-none">Select all</span>
                </div>
            )}
            <div ref={listScrollRef} className="overflow-y-auto max-h-60">
                <ul className="py-1">
                    {filteredRows.length > 0 ? (
                        displayRows.map(row => {
                            const isSelected = selectedIds.includes(row.id);
                            return (
                                <li
                                    key={row.id}
                                    data-selector-row-id={row.id}
                                    className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer flex items-center justify-between gap-2"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleOptionToggle(row);
                                    }}
                                    role="option"
                                    aria-selected={isSelected}
                                >
                                    <div className="flex items-center min-w-0 flex-1">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer pointer-events-none shrink-0"
                                            tabIndex={-1}
                                        />
                                        <span className="truncate">{row.label}</span>
                                    </div>
                                    {row.tags.length > 0 && (
                                        <span className="text-gray-400 text-xs shrink-0 text-right max-w-[50%] truncate">
                                            {row.tags.map((tag, tagIdx) => {
                                                const tagInFilter = parsedFilter.requiredTags.some(
                                                    t => t.toLowerCase() === tag.toLowerCase()
                                                );
                                                return (
                                                    <React.Fragment key={`${row.id}-tag-${tagIdx}`}>
                                                        {tagIdx > 0 ? ', ' : null}
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            title={
                                                                tagInFilter
                                                                    ? `Remove tag "${tag}" from filter`
                                                                    : `Add tag "${tag}" to filter`
                                                            }
                                                            className={[
                                                                'group inline-flex items-baseline rounded px-0.5 align-baseline text-gray-400 transition-colors focus:outline-none focus:ring-1',
                                                                tagInFilter
                                                                    ? 'hover:bg-red-50 hover:text-red-600 focus:ring-red-400'
                                                                    : 'hover:bg-blue-50 hover:text-blue-600 focus:ring-blue-400',
                                                            ].join(' ')}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                applyTagChipAction(tag);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.stopPropagation();
                                                                    e.preventDefault();
                                                                    applyTagChipAction(tag);
                                                                }
                                                            }}
                                                        >
                                                            {renderTagWithFilterHighlight(
                                                                tag,
                                                                parsedFilter.rest,
                                                                tagInFilter
                                                            )}
                                                            <span
                                                                className={[
                                                                    'ml-px font-medium opacity-0 transition-opacity group-hover:opacity-100',
                                                                    tagInFilter ? 'text-red-600' : 'text-blue-600',
                                                                ].join(' ')}
                                                                aria-hidden
                                                            >
                                                                {tagInFilter ? '-' : '+'}
                                                            </span>
                                                        </span>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </span>
                                    )}
                                </li>
                            );
                        })
                    ) : (
                        <li className="px-3 py-2 text-sm text-gray-500 italic">{props.emptyListMessage}</li>
                    )}
                </ul>
            </div>

            {showAdd && (
                <div className="border-t border-gray-200 flex-shrink-0 bg-white">
                    <div
                        className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer flex items-center"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleAddClick(e);
                        }}
                    >
                        <PlusIcon />
                        <span className="ml-2 font-medium">{props.addRowLabel}</span>
                    </div>
                </div>
            )}
        </div>
    ) : null;

    const hasSelection = props.kind === 'teacher'
        ? props.selectedTeachers.length > 0
        : props.selectedGroups.length > 0;

    return (
        <div className="inline-block w-full flex items-center gap-1" ref={dropdownRef}>
            <div className="flex-grow relative" ref={buttonRef}>
                <Button
                    type="button"
                    variant="secondary"
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        if (!props.disabled) {
                            if (!isOpen) {
                                updatePanelPosition();
                                const shouldKeepSingleGroupInPlace = props.kind === 'group' && selectedIds.length === 1;
                                setOpenedRowOrder(
                                    shouldKeepSingleGroupInPlace ? null : rowIdsSelectedFirst(props.rows, selectedIds)
                                );
                                setOpenedScrollTargetId(shouldKeepSingleGroupInPlace ? selectedIds[0]! : null);
                                setIsOpen(true);
                            } else {
                                setIsOpen(false);
                            }
                        }
                    }}
                    disabled={props.disabled}
                    title={props.title}
                    aria-label={props.ariaLabel}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    className="w-full flex items-center text-left px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <span className="flex-grow truncate">{getButtonText()}</span>
                    <ChevronDownIcon />
                </Button>
            </div>

            {hasSelection && !props.disabled && (
                <button
                    type="button"
                    onClick={handleClearSelection}
                    className="p-2 text-gray-500 hover:text-red-600 focus:outline-none focus:ring-1 focus:ring-red-500 rounded-md flex-shrink-0"
                    title="Clear selection"
                    aria-label="Clear selection"
                >
                    <CrossIcon />
                </button>
            )}

            {DropdownPanel && createPortal(DropdownPanel, document.body)}

            {props.kind === 'teacher' && (
                <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Teacher">
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="add-teacher-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <Input
                                id="add-teacher-name"
                                name="name"
                                value={newTeacherData.name}
                                onChange={handleAddTeacherInputChange}
                                placeholder="Teacher's full name"
                                required
                                autoFocus
                                disabled={isAdding}
                                label=""
                                minLength={0}
                            />
                        </div>
                        <div>
                            <label htmlFor="add-teacher-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <Input
                                id="add-teacher-email"
                                name="email"
                                type="email"
                                value={newTeacherData.email}
                                onChange={handleAddTeacherInputChange}
                                placeholder="teacher@example.com"
                                required
                                disabled={isAdding}
                                label=""
                                autoFocus={false}
                                minLength={0}
                            />
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setIsAddModalOpen(false)}
                                disabled={isAdding}
                                title="Cancel adding teacher"
                                aria-label="Cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                disabled={isAdding || !newTeacherData.name.trim() || !newTeacherData.email.trim()}
                                onClick={handleSaveNewTeacher}
                                title="Add new teacher"
                                aria-label="Add teacher"
                            >
                                {isAdding ? 'Adding...' : 'Add Teacher'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {props.kind === 'group' && (
                <Modal isOpen={isAddModalOpen} onClose={() => { setIsAddModalOpen(false); setNewGroupData({ name: '' }); }} title="Add New Group">
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="add-group-name-unified" className="block text-sm font-medium text-gray-700 mb-1">
                                Group Name *
                            </label>
                            <Input
                                id="add-group-name-unified"
                                name="name"
                                value={newGroupData.name}
                                onChange={handleAddGroupInputChange}
                                placeholder="e.g., Cinematography Year 1"
                                required
                                autoFocus
                                disabled={isAdding}
                                label=""
                                minLength={0}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                A unique group code will be generated automatically.
                            </p>
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => { setIsAddModalOpen(false); setNewGroupData({ name: '' }); }}
                                disabled={isAdding}
                                title="Cancel adding group"
                                aria-label="Cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={handleSaveNewGroup}
                                disabled={isAdding || !newGroupData.name.trim()}
                                title="Add new group"
                                aria-label="Add Group"
                            >
                                {isAdding ? 'Adding...' : 'Add Group'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// --- Public wrappers -------------------------------------------------------

const TeacherSelector: React.FC<TeacherSelectorProps> = ({
    teachers,
    selectedTeachers,
    onChange,
    onAddTeacher,
    placeholder = 'Select Teachers',
    disabled = false
}) => {
    const rows = useMemo(() => rowsFromTeachers(teachers), [teachers]);
    return (
        <UnifiedEntitySelector
            kind="teacher"
            rows={rows}
            teachers={teachers}
            selectedTeachers={selectedTeachers}
            onChange={onChange}
            onAddTeacher={onAddTeacher}
            placeholder={placeholder}
            disabled={disabled}
            panelDomId="teacher-selector-panel"
            searchPlaceholder="Filter teachers..."
            addRowLabel="Add New Teacher"
            ariaLabel="Select teachers"
            title="Select teachers"
            emptyListMessage="No teachers available"
            selectAllAriaPrefix="teachers"
        />
    );
};

export const GroupSelector: React.FC<GroupSelectorProps> = ({
    groups,
    selectedGroups,
    onChange,
    onAddGroup,
    placeholder = 'Select Groups',
    disabled = false
}) => {
    const rows = useMemo(() => rowsFromGroups(groups), [groups]);
    return (
        <UnifiedEntitySelector
            kind="group"
            rows={rows}
            groups={groups}
            selectedGroups={selectedGroups}
            onChange={onChange}
            onAddGroup={onAddGroup}
            placeholder={placeholder}
            disabled={disabled}
            panelDomId="group-selector-panel"
            searchPlaceholder="Filter groups..."
            addRowLabel="Add New Group"
            ariaLabel="Select groups"
            title="Select groups"
            emptyListMessage="No groups available"
            selectAllAriaPrefix="groups"
        />
    );
};

export default TeacherSelector;
